import React, { useState } from 'react';
import { useChartData } from './useChartData';
import { useXAxisConfig } from './useXAxisConfig';
import { ChartSection } from './ChartSection';
import { RequestTrendChart } from './RequestTrendChart';
import { CostTrendChart } from './CostTrendChart';
import { ApiKeyTrendChart } from './ApiKeyTrendChart';
import { ModelDistributionChart } from './ModelDistributionChart';
import type { UsageChartsProps } from './types';

// Section icon components (small SVGs used in chart section headers)
const RequestIcon = () => (
  <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);
const CostIcon = () => (
  <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);
const ApiKeyIcon = () => (
  <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);
const ModelIcon = () => (
  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

export const UsageCharts: React.FC<UsageChartsProps> = React.memo(({
  dailyData,
  chartData,
  currency,
  exchangeRate,
  dateRange,
  timezoneMode
}) => {
  // State for collapsible sections
  const [showRequestTrend, setShowRequestTrend] = useState(true);
  const [showCostTrend, setShowCostTrend] = useState(true);
  const [showModelDistribution, setShowModelDistribution] = useState(true);
  const [showApiKeyTrend, setShowApiKeyTrend] = useState(true);
  const [showOverlays, setShowOverlays] = useState(true);

  const handleToggleOverlays = React.useCallback(() => setShowOverlays(v => !v), []);

  const {
    chartSeries,
    requestsData,
    costData,
    apiKeyData,
    uniqueModels,
    modelDistributionData,
    showCostMovingAverage,
    referenceAreas
  } = useChartData({ chartData, dailyData, dateRange, currency, exchangeRate, timezoneMode });

  const {
    formatTimestamp
  } = useXAxisConfig({ chartSeries, requestsData, costData, apiKeyData, dateRange, timezoneMode });

  if (!dailyData || dailyData.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <ChartSection title="Daily Request Trend" icon={<RequestIcon />} isExpanded={showRequestTrend} onToggle={() => setShowRequestTrend(!showRequestTrend)}>
        <RequestTrendChart
          data={requestsData}
          showOverlays={showOverlays}
          referenceAreas={referenceAreas}
          formatTimestamp={formatTimestamp}
        />
      </ChartSection>

      <ChartSection title="Daily Cost Trend" icon={<CostIcon />} isExpanded={showCostTrend} onToggle={() => setShowCostTrend(!showCostTrend)}>
        <CostTrendChart
          data={costData}
          showOverlays={showOverlays}
          referenceAreas={referenceAreas}
          showMovingAverage={showCostMovingAverage}
          formatTimestamp={formatTimestamp}
        />
      </ChartSection>

      <ChartSection title="Daily Unique API Key Trend" icon={<ApiKeyIcon />} isExpanded={showApiKeyTrend} onToggle={() => setShowApiKeyTrend(!showApiKeyTrend)}>
        <ApiKeyTrendChart
          data={apiKeyData}
          showOverlays={showOverlays}
          referenceAreas={referenceAreas}
          formatTimestamp={formatTimestamp}
        />
      </ChartSection>

      {uniqueModels.length > 0 && (
        <ChartSection title="Model Usage Distribution (%)" icon={<ModelIcon />} isExpanded={showModelDistribution} onToggle={() => setShowModelDistribution(!showModelDistribution)}>
          <ModelDistributionChart
            data={modelDistributionData}
            uniqueModels={uniqueModels}
            showOverlays={showOverlays}
            referenceAreas={referenceAreas}
            formatTimestamp={formatTimestamp}
            onToggleOverlays={handleToggleOverlays}
          />
        </ChartSection>
      )}
    </div>
  );
});
UsageCharts.displayName = 'UsageCharts';
